'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { trackPhotoUploadStart, trackPhotoUploadSuccess, trackPhotoUploadError, trackPhotoUpload } from '@/lib/analytics';

interface GuestPhotoUploadProps {
  onUploadSuccess?: () => void;
}

interface FileWithPreview {
  file: File;
  preview: string;
  id: string;
}

const GuestPhotoUpload = ({ onUploadSuccess }: GuestPhotoUploadProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: string }>({});
  
  const [formData, setFormData] = useState({
    submitterName: '',
    submitterEmail: '',
    category: 'other\' as \'ceremony\' | \'reception\' | \'preparation\' | \'family\' | \'other',
    caption: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
    const maxSize = 10485760; // 10MB
    const newFiles: FileWithPreview[] = [];
    const errors: string[] = [];

    files.forEach((file) => {
      // Validate file type
      if (!validTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size exceeds 10MB`);
        return;
      }

      // Create preview
      const reader = new FileReader();
      const fileId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;
      
      reader.onloadend = () => {
        newFiles.push({
          file,
          preview: reader.result as string,
          id: fileId,
        });

        // Update state when all files are processed
        if (newFiles.length === files.length - errors.length) {
          setSelectedFiles((prev) => [...prev, ...newFiles]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    // Reset input
    e.target.value = '';
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('Please select at least one photo to upload');
      return;
    }

    trackPhotoUploadStart();
    setIsUploading(true);
    setError(null);
    setUploadProgress({});

    try {
      const supabase = createClient();
      let successCount = 0;
      let failCount = 0;
      const totalSize = selectedFiles.reduce((sum, f) => sum + f.file.size, 0);

      // Upload each file
      for (const fileItem of selectedFiles) {
        try {
          setUploadProgress((prev) => ({ ...prev, [fileItem.id]: 'Uploading...' }));

          // Generate unique file path
          const fileExt = fileItem.file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const storagePath = `${formData.category}/${fileName}`;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('guest-photos')
            .upload(storagePath, fileItem.file, {
              cacheControl: '3600',
              upsert: false,
            });

          if (uploadError) throw uploadError;

          // Create database record
          const { error: dbError } = await supabase
            .from('guest_photos')
            .insert({
              submitter_name: formData.submitterName,
              submitter_email: formData.submitterEmail,
              storage_path: storagePath,
              file_name: fileItem.file.name,
              file_size: fileItem.file.size,
              mime_type: fileItem.file.type,
              category: formData.category,
              caption: formData.caption || null,
              status: 'pending',
            });

          if (dbError) throw dbError;

          setUploadProgress((prev) => ({ ...prev, [fileItem.id]: 'Success' }));
          successCount++;
        } catch (err: any) {
          console.error(`Upload error for ${fileItem.file.name}:`, err);
          setUploadProgress((prev) => ({ ...prev, [fileItem.id]: 'Failed' }));
          failCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        trackPhotoUploadSuccess(successCount);
        trackPhotoUpload({
          category: formData.category,
          fileCount: successCount,
          totalSize: totalSize,
        });
        setUploadSuccess(true);
        setFormData({
          submitterName: '',
          submitterEmail: '',
          category: 'other',
          caption: '',
        });
        setSelectedFiles([]);

        // Call success callback
        if (onUploadSuccess) {
          onUploadSuccess();
        }

        // Reset success message after 5 seconds
        setTimeout(() => {
          setUploadSuccess(false);
          setIsOpen(false);
          setUploadProgress({});
        }, 5000);
      }

      if (failCount > 0) {
        const errorMsg = `${failCount} photo(s) failed to upload. Please try again.`;
        setError(errorMsg);
        trackPhotoUploadError(errorMsg);
      }

    } catch (err: any) {
      console.error('Upload error:', err);
      const errorMsg = err.message || 'Failed to upload photos. Please try again.';
      setError(errorMsg);
      trackPhotoUploadError(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setUploadSuccess(false);
    setError(null);
    setSelectedFiles([]);
    setUploadProgress({});
  };

  return (
    <>
      {/* Upload Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-6 py-3 bg-primary text-white font-inter font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-sacred-md hover:shadow-sacred-lg"
      >
        <Icon name="CameraIcon" size={20} className="mr-2" />
        Upload Your Photos
      </button>

      {/* Upload Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50 backdrop-blur-sm">
          <div className="bg-card rounded-lg shadow-sacred-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="font-playfair text-2xl font-semibold text-foreground">
                Share Your Wedding Moments
              </h2>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            {/* Success Message */}
            {uploadSuccess && (
              <div className="m-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex items-start">
                  <Icon name="CheckCircleIcon" size={24} className="text-primary mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-inter text-sm font-medium text-foreground mb-1">
                      Photos Submitted Successfully!
                    </p>
                    <p className="font-inter text-xs text-muted-foreground">
                      Your photos will appear in the gallery once approved by our family coordinators.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <Icon name="ExclamationTriangleIcon" size={24} className="text-red-600 mr-3 flex-shrink-0" />
                  <p className="font-inter text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <label className="block font-inter text-sm font-medium text-foreground mb-2">
                  Select Photos *
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
                  {selectedFiles.length > 0 ? (
                    <div className="space-y-4">
                      {/* Preview Grid */}
                      <div className="grid grid-cols-3 gap-4">
                        {selectedFiles.map((fileItem) => (
                          <div key={fileItem.id} className="relative group">
                            <img
                              src={fileItem.preview}
                              alt="Preview"
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => removeFile(fileItem.id)}
                              disabled={isUploading}
                              className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            >
                              <Icon name="XMarkIcon" size={16} />
                            </button>
                            {/* Upload Status */}
                            {uploadProgress[fileItem.id] && (
                              <div className="absolute inset-0 bg-foreground/80 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xs font-medium">
                                  {uploadProgress[fileItem.id]}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Add More Button */}
                      <label className="cursor-pointer inline-block">
                        <span className="font-inter text-sm text-primary hover:text-primary/80">
                          + Add More Photos
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic"
                          multiple
                          onChange={handleFileSelect}
                          disabled={isUploading}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <div>
                      <Icon name="PhotoIcon" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <label className="cursor-pointer">
                        <span className="font-inter text-sm text-primary hover:text-primary/80">
                          Click to upload multiple photos
                        </span>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                      <p className="font-inter text-xs text-muted-foreground mt-2">
                        JPEG, PNG, WebP, or HEIC (Max 10MB per file)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block font-inter text-sm font-medium text-foreground mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.submitterName}
                  onChange={(e) => setFormData({ ...formData, submitterName: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block font-inter text-sm font-medium text-foreground mb-2">
                  Your Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.submitterEmail}
                  onChange={(e) => setFormData({ ...formData, submitterEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block font-inter text-sm font-medium text-foreground mb-2">
                  Photo Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  className="w-full px-4 py-2 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="ceremony">Ceremony</option>
                  <option value="reception">Reception</option>
                  <option value="preparation">Preparation</option>
                  <option value="family">Family</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Caption */}
              <div>
                <label className="block font-inter text-sm font-medium text-foreground mb-2">
                  Caption (Optional)
                </label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Add a caption to your photos..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isUploading}
                  className="px-6 py-2 border border-border rounded-lg font-inter text-sm font-medium text-foreground hover:bg-surface transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading || selectedFiles.length === 0}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-inter text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isUploading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={16} className="mr-2 animate-spin" />
                      Uploading {selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''}...
                    </>
                  ) : (
                    `Submit ${selectedFiles.length} Photo${selectedFiles.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default GuestPhotoUpload;